import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import { StarButton } from './StarButton.js';
import type { PostChartPoint, PostDetail } from '@wml-crm/shared';

interface Props {
  point: PostChartPoint;
  onClose: () => void;
  /** Уведомить родителя, когда пользователь кликнул звёздочку.
   *  Родитель обновляет свой список без F5. */
  onReuseChanged?: (postId: string, count: number) => void;
}

function fmtConv(x: number | null): string {
  if (x == null) return '—';
  return `1/${Number.isInteger(x) ? x : x.toFixed(1)}`;
}

export function PostDrawer({ point, onClose, onReuseChanged }: Props) {
  const [detail, setDetail] = useState<PostDetail | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [reuseCount, setReuseCount] = useState<number>(point.reuseCount ?? 0);
  const copyTimer = useRef<number | null>(null);

  useEffect(() => {
    setDetail(null);
    api<PostDetail>(`/api/posts/${point.postId}`)
      .then((d) => {
        setDetail(d);
        const firstHeader = d.screenshots.find((s) => s.kind === 'header');
        setSelectedDate(firstHeader?.date ?? d.screenshots[0]?.date ?? null);
        if (typeof d.post.reuseCount === 'number') setReuseCount(d.post.reuseCount);
      })
      .catch(() => setDetail(null));
  }, [point.postId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const p = detail?.post ?? point;
  // Скрин на пост один (не по-снапшотный список). Оставили state selectedDate
  // для совместимости, но фактически берём первый доступный header.
  void selectedDate;
  const shownHeader = (detail?.screenshots ?? []).find((s) => s.kind === 'header');

  // Скрины из Stats DM не хранятся дольше 3 месяцев — для старых постов
  // всегда показываем ссылку «смотри в чате Twitter Stata V LS за дату».
  const THREE_MONTHS_MS = 90 * 86_400_000;
  const reportDateMs = new Date(p.lastReportDate).getTime();
  const isOldPost = Number.isFinite(reportDateMs) && (Date.now() - reportDateMs) > THREE_MONTHS_MS;
  const showFallback = !shownHeader || isOldPost;
  const fallbackDate = new Date(p.lastReportDate).toLocaleDateString('ru-RU', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{p.slaveName}</span>
              <button
                className="copy-slave-btn"
                title={copied ? 'Скопировано' : `Копировать: ${p.slaveName}`}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(p.slaveName);
                    setCopied(true);
                    if (copyTimer.current) window.clearTimeout(copyTimer.current);
                    copyTimer.current = window.setTimeout(() => setCopied(false), 1400);
                  } catch { /* ignore */ }
                }}
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="12" height="12" rx="2" />
                    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                  </svg>
                )}
                {copied ? 'скопировано' : 'копировать'}
              </button>
            </div>
            <div style={{ color: 'var(--text-subtle)', fontSize: 12 }}>
              Пост {new Date(p.postDate).toLocaleDateString('ru-RU')} · последний отчёт{' '}
              {new Date(p.lastReportDate).toLocaleDateString('ru-RU')}
            </div>
          </div>
          <StarButton
            postId={point.postId}
            count={reuseCount}
            onChange={(next) => {
              setReuseCount(next);
              onReuseChanged?.(point.postId, next);
            }}
          />
          <button className="icon-btn" title="Закрыть (Esc)" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="drawer-body">
          <div className="kv">
            <div className="k">Охват</div><div><b>{p.views.toLocaleString('ru-RU')}</b></div>
            {p.retweets > 0 && (<><div className="k">Ретвитов</div><div>{p.retweets.toLocaleString('ru-RU')}</div></>)}
            <div className="k">Дней в работе</div><div>{p.daysInWork}</div>
            <div className="k">Конверт день-1</div><div><b>{fmtConv(p.conversionDay1)}</b></div>
            {p.daysInWork > 1 && <><div className="k">Конверт день-2</div><div>{fmtConv(p.conversionDay2)}</div></>}
            {p.daysInWork > 2 && <><div className="k">Средний</div><div>{fmtConv(p.conversionAvg)}</div></>}
          </div>

          {p.postUrl && (
            <a href={p.postUrl} target="_blank" rel="noreferrer" className="link-out">
              Открыть пост в Twitter →
            </a>
          )}

          {!showFallback && shownHeader ? (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                Скрин отчёта ({new Date(shownHeader.date).toLocaleDateString('ru-RU')})
              </div>
              <img src={shownHeader.url} alt="header screenshot" />
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 32, background: 'var(--bg-mute)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>📷</div>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4, fontSize: 14 }}>
                Фото недоступно
              </div>
              <div style={{ fontSize: 13 }}>
                Смотри по дате <b style={{ color: 'var(--text)' }}>{fallbackDate}</b> в чате Twitter Stata V LS.
              </div>
              {isOldPost && (
                <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 8 }}>
                  (скрины хранятся только 3 месяца)
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
